import React, { useState, useEffect } from 'react';
import { 
  Users, DollarSign, Calendar, Phone, Mail, Building, 
  Plus, Search, Filter, ChevronRight, MoreVertical,
  Edit2, Trash2, MessageSquare, ArrowRight, Clock,
  CheckCircle, XCircle, Target, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-slate-500', textColor: 'text-slate-400' },
  demo_scheduled: { label: 'Demo Scheduled', color: 'bg-blue-500', textColor: 'text-blue-400' },
  quoted: { label: 'Quoted', color: 'bg-purple-500', textColor: 'text-purple-400' },
  subscribed: { label: 'Subscribed', color: 'bg-green-500', textColor: 'text-green-400' },
  lost: { label: 'Lost', color: 'bg-red-500', textColor: 'text-red-400' }
};

const LEAD_SOURCES = [
  { value: 'website', label: 'Website' },
  { value: 'signup', label: 'Signup' },
  { value: 'early_access', label: 'Early Access' },
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'contractor_application', label: 'Contractor App' },
  { value: 'referral', label: 'Referral' },
  { value: 'ad', label: 'Advertisement' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'cold_outreach', label: 'Cold Outreach' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' }
];

const COMPANY_SIZES = [
  { value: '1-2 crews', label: '1-2 Crews' },
  { value: '3-5 crews', label: '3-5 Crews' },
  { value: '6-10 crews', label: '6-10 Crews' },
  { value: '10+ crews', label: '10+ Crews' }
];

export default function PlatformSalesPage() {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [filterStage, setFilterStage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [filterStage]);

  const fetchProspects = async () => {
    try {
      let url = `${API_URL}/api/platform-sales/prospects`;
      if (filterStage) url += `?stage=${filterStage}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProspects(data.prospects || []);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      toast.error('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-sales/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const moveProspect = async (prospectId, newStage) => {
    try {
      const response = await fetch(`${API_URL}/api/platform-sales/prospects/${prospectId}/move-stage?new_stage=${newStage}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Prospect moved to ${STAGE_CONFIG[newStage].label}`);
        fetchProspects();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to move prospect');
    }
  };

  const deleteProspect = async (prospectId) => {
    if (!window.confirm('Are you sure you want to delete this prospect?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/platform-sales/prospects/${prospectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Prospect deleted');
        fetchProspects();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete prospect');
    }
  };

  const filteredProspects = prospects.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.first_name?.toLowerCase().includes(search) ||
      p.last_name?.toLowerCase().includes(search) ||
      p.email?.toLowerCase().includes(search) ||
      p.company?.toLowerCase().includes(search)
    );
  });

  const getProspectsByStage = (stage) => {
    return filteredProspects.filter(p => p.stage === stage);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Target className="w-7 h-7 text-cyan-400" />
                Platform Sales Pipeline
              </h1>
              <p className="text-slate-400 mt-1">Track HomeFixer360 prospects and conversions</p>
            </div>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-cyan-600 hover:bg-cyan-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Prospect
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Total Prospects</p>
                    <p className="text-2xl font-bold">{stats.total_prospects}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Pipeline Value</p>
                    <p className="text-2xl font-bold">${(stats.total_pipeline_value || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Closed Won</p>
                    <p className="text-2xl font-bold">{stats.by_stage?.closed_won?.count || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Overdue Follow-ups</p>
                    <p className="text-2xl font-bold">{stats.overdue_follow_ups || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-4 mt-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search prospects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
              />
            </div>
            <select
              value={filterStage}
              onChange={(e) => setFilterStage(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm"
            >
              <option value="">All Stages</option>
              {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <div className="flex bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1 rounded text-sm ${viewMode === 'kanban' ? 'bg-slate-700' : ''}`}
              >
                Kanban
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 rounded text-sm ${viewMode === 'list' ? 'bg-slate-700' : ''}`}
              >
                List
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : viewMode === 'kanban' ? (
          /* Kanban View */
          <div className="flex gap-4 overflow-x-auto pb-4">
            {Object.entries(STAGE_CONFIG).map(([stage, config]) => (
              <div key={stage} className="flex-shrink-0 w-72">
                <div className="bg-slate-900 rounded-xl border border-slate-800">
                  <div className="p-4 border-b border-slate-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
                        <span className="font-medium">{config.label}</span>
                      </div>
                      <span className="text-slate-400 text-sm">
                        {getProspectsByStage(stage).length}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 max-h-[600px] overflow-y-auto">
                    {getProspectsByStage(stage).map(prospect => (
                      <ProspectCard 
                        key={prospect.id} 
                        prospect={prospect}
                        onMove={moveProspect}
                        onEdit={() => setSelectedProspect(prospect)}
                        onDelete={() => deleteProspect(prospect.id)}
                      />
                    ))}
                    {getProspectsByStage(stage).length === 0 && (
                      <div className="text-center py-8 text-slate-500 text-sm">
                        No prospects
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-slate-900 rounded-xl border border-slate-800">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-4 text-slate-400 font-medium">Contact</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Company</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Stage</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Value</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Source</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProspects.map(prospect => (
                  <tr key={prospect.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
                        <p className="text-sm text-slate-400">{prospect.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p>{prospect.company}</p>
                      <p className="text-sm text-slate-400">{prospect.company_size}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${STAGE_CONFIG[prospect.stage]?.color} bg-opacity-20 ${STAGE_CONFIG[prospect.stage]?.textColor}`}>
                        {STAGE_CONFIG[prospect.stage]?.label}
                      </span>
                    </td>
                    <td className="p-4">
                      {prospect.deal_value ? `$${prospect.deal_value.toLocaleString()}` : '-'}
                    </td>
                    <td className="p-4 text-slate-400 capitalize">{prospect.lead_source}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => setSelectedProspect(prospect)}
                          className="p-1 hover:bg-slate-700 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => deleteProspect(prospect.id)}
                          className="p-1 hover:bg-slate-700 rounded text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || selectedProspect) && (
        <ProspectModal
          prospect={selectedProspect}
          onClose={() => {
            setShowAddModal(false);
            setSelectedProspect(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setSelectedProspect(null);
            fetchProspects();
            fetchStats();
          }}
          token={token}
        />
      )}
    </div>
  );
}

function ProspectCard({ prospect, onMove, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  
  return (
    <div className="bg-slate-800 rounded-lg p-3 hover:bg-slate-800/80 cursor-pointer group">
      <div className="flex items-start justify-between">
        <div onClick={onEdit}>
          <p className="font-medium">{prospect.first_name} {prospect.last_name}</p>
          <p className="text-sm text-slate-400">{prospect.company}</p>
        </div>
        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-slate-400" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-slate-700 rounded-lg shadow-xl py-1 z-10 min-w-[140px]">
              <button 
                onClick={(e) => { e.stopPropagation(); onEdit(); setShowMenu(false); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-600 text-sm flex items-center gap-2"
              >
                <Edit2 className="w-3 h-3" /> Edit
              </button>
              <div className="border-t border-slate-600 my-1"></div>
              <p className="px-3 py-1 text-xs text-slate-400">Move to:</p>
              {Object.entries(STAGE_CONFIG).map(([stage, config]) => (
                stage !== prospect.stage && (
                  <button
                    key={stage}
                    onClick={(e) => { e.stopPropagation(); onMove(prospect.id, stage); setShowMenu(false); }}
                    className="w-full text-left px-3 py-1 hover:bg-slate-600 text-sm"
                  >
                    {config.label}
                  </button>
                )
              ))}
              <div className="border-t border-slate-600 my-1"></div>
              <button 
                onClick={(e) => { e.stopPropagation(); onDelete(); setShowMenu(false); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-600 text-sm text-red-400 flex items-center gap-2"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-2 flex items-center gap-3 text-xs text-slate-400 flex-wrap">
        <span className="flex items-center gap-1">
          <Mail className="w-3 h-3" />
          {prospect.email?.split('@')[0]}...
        </span>
        {prospect.lead_source && (
          <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 text-[10px]">
            {LEAD_SOURCES.find(s => s.value === prospect.lead_source)?.label || prospect.lead_source}
          </span>
        )}
        {prospect.deal_value && (
          <span className="flex items-center gap-1 text-green-400">
            <DollarSign className="w-3 h-3" />
            {prospect.deal_value.toLocaleString()}
          </span>
        )}
      </div>
      
      {prospect.follow_up_date && (
        <div className="mt-2 flex items-center gap-1 text-xs text-amber-400">
          <Clock className="w-3 h-3" />
          Follow-up: {new Date(prospect.follow_up_date).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function ProspectModal({ prospect, onClose, onSave, token }) {
  const [formData, setFormData] = useState({
    first_name: prospect?.first_name || '',
    last_name: prospect?.last_name || '',
    email: prospect?.email || '',
    phone: prospect?.phone || '',
    company: prospect?.company || '',
    company_size: prospect?.company_size || '',
    lead_source: prospect?.lead_source || 'website',
    deal_value: prospect?.deal_value || '',
    notes: prospect?.notes || '',
    comment: prospect?.comment || '',
    how_can_we_help: prospect?.how_can_we_help || '',
    stage: prospect?.stage || 'new'
  });
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const url = prospect 
        ? `${API_URL}/api/platform-sales/prospects/${prospect.id}`
        : `${API_URL}/api/platform-sales/prospects`;
      
      const response = await fetch(url, {
        method: prospect ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
        })
      });
      
      if (response.ok) {
        toast.success(prospect ? 'Prospect updated' : 'Prospect created');
        onSave();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save prospect');
      }
    } catch (error) {
      toast.error('Failed to save prospect');
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !prospect) return;
    
    try {
      const response = await fetch(`${API_URL}/api/platform-sales/prospects/${prospect.id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newNote })
      });
      
      if (response.ok) {
        toast.success('Note added');
        setNewNote('');
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-xl font-bold">
            {prospect ? 'Edit Prospect' : 'Add New Prospect'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">First Name *</label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                required
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Last Name *</label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                required
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Company *</label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                required
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Company Size</label>
              <select
                value={formData.company_size}
                onChange={(e) => setFormData({...formData, company_size: e.target.value})}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
              >
                <option value="">Select size...</option>
                {COMPANY_SIZES.map(size => (
                  <option key={size.value} value={size.value}>{size.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Lead Source</label>
              <select
                value={formData.lead_source}
                onChange={(e) => setFormData({...formData, lead_source: e.target.value})}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
              >
                {LEAD_SOURCES.map(source => (
                  <option key={source.value} value={source.value}>{source.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({...formData, stage: e.target.value})}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
              >
                {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Deal Value ($)</label>
              <Input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({...formData, deal_value: e.target.value})}
                placeholder="0"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 resize-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Comment</label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                rows={2}
                placeholder="Optional comment..."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">How can we help?</label>
              <textarea
                value={formData.how_can_we_help}
                onChange={(e) => setFormData({...formData, how_can_we_help: e.target.value})}
                rows={2}
                placeholder="What are they looking for?"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 resize-none"
              />
            </div>
          </div>
          
          {/* Activity Log (for existing prospects) */}
          {prospect && prospect.activity_log && prospect.activity_log.length > 0 && (
            <div className="border-t border-slate-800 pt-4">
              <h3 className="font-medium mb-3">Activity Log</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[...prospect.activity_log].reverse().slice(0, 10).map((activity, idx) => (
                  <div key={idx} className="flex gap-3 text-sm">
                    <span className="text-slate-500">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </span>
                    <span className="text-slate-300">{activity.details}</span>
                  </div>
                ))}
              </div>
              
              {/* Add Note */}
              <div className="flex gap-2 mt-3">
                <Input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="bg-slate-800 border-slate-700"
                />
                <Button type="button" onClick={addNote} variant="outline">
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-cyan-600 hover:bg-cyan-500">
              {saving ? 'Saving...' : (prospect ? 'Update' : 'Create')} Prospect
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
